import * as React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Modal,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useParams } from 'react-router';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

export default function MediaEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<string | null>(null);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');

  const [tags, setTags] = React.useState<any[]>([]);
  const [allTags, setAllTags] = React.useState<any[]>([]);
  const [tagsOpen, setTagsOpen] = React.useState(false);

  React.useEffect(() => {
    fetch(`${API_URL}/media/${id}`)
      .then((r) => r.json())
      .then((data) => {
        setPreview(`${IMAGE_URL}${data.imageUrl}`);
        setTitle(data.title || '');
        setDescription(data.description || '');
        setTags(data.Tags || []);
      });

    fetch(`${API_URL}/tags`)
      .then((r) => r.json())
      .then((data) => setAllTags(data.data));
  }, [id]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onSubmit = async () => {
    const formData = new FormData();
    if (file) formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);

    await fetch(`${API_URL}/media/${id}`, {
      method: 'PUT',
      body: formData,
    });

    navigate('/media');
  };

  const addTag = async (tagId: number) => {
    await fetch(`${API_URL}/media/${id}/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tagId }),
    });

    const res = await fetch(`${API_URL}/media/${id}`);
    const data = await res.json();
    setTags(data.Tags || []);
  };

  const removeTag = async (tagId: number) => {
    await fetch(`${API_URL}/media/${id}/tags/${tagId}`, {
      method: 'DELETE',
    });

    setTags((prev) => prev.filter((t) => t.tagId !== tagId));
  };

  return (
    <Box sx={{ mx: 10, mt: 4 }}>
      <Typography variant="h5" mb={2}>
        Редагувати медіа
      </Typography>

      <Button variant="contained" component="label">
        Змінити файл
        <input hidden type="file" accept="image/*" onChange={onFileChange} />
      </Button>

      {preview && (
        <Card sx={{ mt: 3 }}>
          <CardMedia
            component="img"
            image={preview}
            sx={{ width: 300, height: 300 }}
          />
        </Card>
      )}

      <TextField
        fullWidth
        label="Назва"
        sx={{ mt: 3 }}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <TextField
        fullWidth
        label="Опис"
        multiline
        rows={4}
        sx={{ mt: 3 }}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" mb={1}>
          Теги
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {tags.map((t) => (
            <Box
              key={t.tagId}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                bgcolor: t.color,
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: t.color,
                  border: '2px solid #fff',
                }}
              />

              <Typography variant="caption">{t.name}</Typography>

              <IconButton size="small" onClick={() => removeTag(t.tagId)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Button
            variant="outlined"
            size="small"
            onClick={() => setTagsOpen(true)}
          >
            +
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={onSubmit}>
          Зберегти
        </Button>
        <Button variant="outlined" onClick={() => navigate('/media')}>
          Скасувати
        </Button>
      </Box>

      <Modal open={tagsOpen} onClose={() => setTagsOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            p: 3,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={2}>
            Додати теги
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {allTags.map((t) => {
              const exists = tags.some((x) => x.tagId === t.tagId);

              return (
                <Box
                  key={t.tagId}
                  onClick={() => !exists && addTag(t.tagId)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: t.color,
                    opacity: exists ? 0.4 : 1,
                    cursor: exists ? 'not-allowed' : 'pointer',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: t.color,
                      border: '2px solid #fff',
                    }}
                  />

                  <Typography variant="caption">{t.name}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
